"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";

interface ConfirmDeleteDialogProps {
    /** Dialog title, e.g. "Delete Story" */
    title: string;
    /** Descriptive warning message */
    description: string;
    /** Async callback invoked when the user clicks "Delete Forever" */
    onConfirm: () => Promise<void>;
    /** The trigger element that opens the dialog */
    trigger: React.ReactNode;
    /** Controlled open state (optional — used when trigger is external) */
    open?: boolean;
    /** Controlled onOpenChange (optional) */
    onOpenChange?: (open: boolean) => void;
}

export function ConfirmDeleteDialog({
    title,
    description,
    onConfirm,
    trigger,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
}: ConfirmDeleteDialogProps) {
    const [internalOpen, setInternalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : internalOpen;
    const setOpen = isControlled
        ? (v: boolean) => controlledOnOpenChange?.(v)
        : setInternalOpen;

    async function handleConfirm() {
        setError(null);
        setLoading(true);
        try {
            await onConfirm();
            setLoading(false);
            setOpen(false);
        } catch (err) {
            // A server action's redirect() surfaces here as a thrown NEXT_REDIRECT.
            // That's success, not failure — let it propagate so navigation happens.
            if (
                err &&
                typeof err === "object" &&
                "digest" in err &&
                typeof (err as { digest?: unknown }).digest === "string" &&
                (err as { digest: string }).digest.startsWith("NEXT_REDIRECT")
            ) {
                throw err;
            }
            // Real failure — surface it instead of silently swallowing, so the user
            // knows the delete didn't happen and can retry.
            setError("Something went wrong and this wasn't deleted. Please try again.");
            setLoading(false);
        }
    }

    function handleOpenChange(nextOpen: boolean) {
        if (loading) return;
        setOpen(nextOpen);
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                {error && (
                    <p role="alert" className="text-sm text-destructive mt-2">
                        {error}
                    </p>
                )}
                <div className="flex justify-end gap-2 mt-4">
                    <Button
                        variant="outline"
                        onClick={() => setOpen(false)}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleConfirm}
                        disabled={loading}
                    >
                        {loading ? "Deleting..." : "Delete Forever"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
